import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface Props {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  id?: string;
  maxResults?: number;
}

/**
 * Diacritics-insensitive substring matching:
 * "gorczewska" → "Górczewska"
 * "marszal"    → "Marszałkowska"
 * "zolibor"    → "Żoliborz"
 */
function normalize(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export default function StreetCombobox({
  value,
  onChange,
  options,
  placeholder,
  id,
  maxResults = 60,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const q = normalize(value.trim());
    if (!q) return [];
    return options
      .filter((o) => normalize(o).includes(q))
      .slice(0, maxResults);
  }, [value, options, maxResults]);

  useEffect(() => {
    setActiveIndex(0);
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const active = listRef.current.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const selectIndex = useCallback(
    (idx: number) => {
      const picked = filtered[idx];
      if (picked) {
        onChange(picked);
        setIsOpen(false);
      }
    },
    [filtered, onChange]
  );

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (isOpen && filtered.length > 0) {
        e.preventDefault();
        selectIndex(activeIndex);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen && filtered.length > 0}
        aria-autocomplete="list"
        aria-controls={id ? `${id}-list` : undefined}
        aria-activedescendant={
          isOpen && filtered.length > 0 && id ? `${id}-opt-${activeIndex}` : undefined
        }
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none"
      />
      {isOpen && filtered.length > 0 && (
        <ul
          ref={listRef}
          id={id ? `${id}-list` : undefined}
          role="listbox"
          className="absolute z-10 mt-1 w-full max-h-64 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {filtered.map((opt, idx) => (
            <li
              key={opt}
              id={id ? `${id}-opt-${idx}` : undefined}
              role="option"
              aria-selected={idx === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                selectIndex(idx);
              }}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`px-4 py-2 cursor-pointer text-sm ${
                idx === activeIndex ? "bg-brand-50 text-brand" : "text-gray-800"
              }`}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
